<template>
  <section class="rcm-card" :class="{ 'is-closed': !open, 'is-edited': c.edited > 0 }" :data-meal="c.name">
    <button type="button" class="rcm-card__head" :aria-expanded="String(open)" @click="ctl.setExpanded(c.name, !open)">
      <v-icon size="18">mdi-chevron-down</v-icon>
      <span class="rcm-card__title">
        <b><recipe-hit :text="toLatin(c.name)" :query="query"></recipe-hit></b>
        <span>{{ subtitle }}</span>
      </span>
      <span class="rcm-card__count rcp-num">
        <b>{{ ctl.fq(c.portions) }}</b>
        <span>{{ ctl.pluralT("recipes_shell.portion_word", c.portions) }}</span>
      </span>
    </button>

    <div v-if="open" class="rcm-card__body">
      <div class="rcm-steps" :class="{ 'is-wide': c.cols.length > 3 }" :style="{ '--n': c.cols.length }">
        <label v-for="col in c.cols" :key="col.shift">
          <report-shift-tag :shift="col.shift"></report-shift-tag>
          <portion-field
            stepper
            :value="col.count"
            :edited="col.edited"
            :shift="col.shift"
            :label="$t('recipes_shell.portions_for', { meal: toLatin(c.name), shift: col.shift })"
            :title="$t('recipes_shell.ordered', { n: col.base })"
            @input="ctl.editPortions(c.name, col.shift, $event)"
          ></portion-field>
        </label>
      </div>

      <div v-if="!c.hasRecipe" class="rcm-none">
        <b>{{ $t("recipes_shell.no_recipe") }}</b>
        {{ $t("recipes_shell.no_recipe_text") }}
        <button v-if="ctl.canAddRecipe" type="button" class="ui-btn ui-btn--sm" @click="ctl.addRecipe">
          {{ $t("recipes_shell.add_recipe") }}
          <v-icon size="14">mdi-open-in-new</v-icon>
        </button>
      </div>
      <template v-else>
        <div v-for="(row, i) in rows" :key="`${row.id}-${i}`" class="rcm-ing">
          <div class="rcm-ing__l">
            <b><recipe-hit :text="toLatin(row.name)" :query="query"></recipe-hit></b>
            <span>{{ perShift(row) }}</span>
          </div>
          <div class="rcm-ing__r rcp-num">
            <b>{{ ctl.fq(row.total) }}</b>
            <span class="rcm-unit">&nbsp;{{ row.measure }}</span>
            <span v-if="ctl.price" class="rcm-ing__cost">{{ row.cost == null ? "—" : money(row.cost) }}</span>
          </div>
        </div>
        <div v-if="ctl.price" class="rcm-foot">
          <span>{{ $t("recipes_shell.per_portion_value", { value: money(c.perPortion) }) }}</span>
          <b>{{ money(c.cost) }}</b>
        </div>
      </template>

      <div v-if="c.edited" class="rcm-foot is-edited">
        <span>{{ editedText }}</span>
        <button type="button" class="rcm-link" @click="ctl.revertMeal(c.name)">{{ $t("recipes_shell.revert") }}</button>
      </div>
    </div>
  </section>
</template>

<script>
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import PortionField from "../parts/PortionField.vue";
import RecipeHit from "../parts/RecipeHit.vue";

/**
 * Kartica jela na telefonu: sklopljena pokazuje naziv, broj namirnica, porcije po smjenama i ukupno; otvorena ima polja
 * "Porcije" sa dugmadima − i + (44 px), namirnice sa količinom po smjenama i (kad su cijene uključene) trošak.
 */
export default {
  name: "RecipesMobileCard",
  components: { ReportShiftTag, PortionField, RecipeHit },
  inject: { ctl: "recipesCtl" },
  props: {
    c: { type: Object, required: true },
    rows: { type: Array, required: true },
    index: { type: Number, default: 0 },
    query: { type: String, default: "" },
    /** Pogodak pretrage je u nazivu jela (ne samo u namirnici). */
    mealHit: { type: Boolean, default: false },
  },
  computed: {
    /**
     * Otvorena je kad je korisnik otvori; do tada prva kartica je otvorena, a pri pretrazi one u kojima je pogodak
     * u namirnici (da se vidi istaknuti red). Pogodak u nazivu jela ne otvara sve kartice odjednom.
     */
    open() {
      const chosen = this.ctl.expanded[this.c.name];
      if (chosen != null) {
        return chosen;
      }
      return this.query ? !this.mealHit : this.index === 0;
    },
    subtitle() {
      const base = this.c.hasRecipe ? this.ctl.pluralT("recipes_shell.groceries_n", this.c.rows.length) : this.$t("recipes_shell.no_recipe");
      if (this.open) {
        return base;
      }
      const cols = this.c.cols.map((col) => this.$t("report_mobile.shift_short", { n: col.shift }) + " " + this.ctl.fq(col.count));
      return `${base} · ${cols.join(" · ")}`;
    },
    editedText() {
      const list = this.c.cols
        .filter((col) => col.edited)
        .map((col) => `${this.$t("report_mobile.shift_short", { n: col.shift })} ${this.ctl.sign(col.delta)}`);
      return `${this.$t("recipes_shell.edited")}: ${list.join(", ")}`;
    },
  },
  methods: {
    money(value) {
      return [this.ctl.fm(value), this.ctl.currency].filter(Boolean).join(" ");
    },
    /** "Sm. 1 40 · Sm. 2 12" (smjene bez količine se izostavljaju). */
    perShift(row) {
      const parts = row.shown
        .map((value, i) => (value ? `${this.$t("report_mobile.shift_short", { n: this.c.cols[i].shift })} ${this.ctl.fq(value)}` : ""))
        .filter(Boolean);
      return parts.length ? parts.join(" · ") : "—";
    },
  },
};
</script>
